import { useField } from "formik";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { generateId, getValueByKey } from "../../../helper/helpers";
import CustomDropdown from "../common/dropdown";
import DropdownMenu from "../common/dropdownMenu";
import { SingleSelectIcon } from "../common/icon";
import SearchBox from "../common/searchBox";
import { filterOptions, sorter } from "../helpers";
import "../styles.scss";

const SingleSelect = (props) => {
  const {
    allowClear = true,
    customClass,
    data: passedData = [],
    disabled = false,
    id,
    infiniteScroll = false,
    initialOptions = [],
    loadMoreData,
    onBlur,
    onChange,
    optionLabelProp = "title",
    optionValueProp = "value",
    placeholder,
    required = false,
    showAll = false,
    showIcons = true,
    showSearch = true,
    sort = true,
    style,
    title,
    defaultValue = { [optionValueProp]: "", [optionLabelProp]: "" },
    value = defaultValue,
    isDataLoading,
  } = props;
  const [state, setState] = useState(() => {
    return {
      count: 0,
      dropdownVisible: false,
      error: false,
      loading: false,
      pageNumber: 2,
      searchQuery: "",
      uuid: generateId(10, true),
    };
  });

  const { dropdownVisible, searchQuery = "", uuid } = state;
  const { [optionLabelProp]: selectedItemLabel = placeholder } = value || {}; // {id: "option1", name: "Option Name"}
  const selItemValue = getValueByKey(value, optionValueProp) || "";

  const inputRef = useRef();
  const listRef = useRef();
  const stateRef = useRef({});
  stateRef.current = state;
  const reachedBottom = useRef(false);
  const ref = useRef({ inputRef, listRef, stateRef, reachedBottom });

  const enableSearch = infiniteScroll
    ? showSearch
    : showSearch &&
      passedData.length + (showAll ? 1 : 0) + initialOptions.length > 5;
  const displayValue = selItemValue ? selectedItemLabel : placeholder;
  const dataSorter = sorter(optionLabelProp);

  const data = useMemo(
    () => {
      const allData = [];

      if (
        showAll &&
        !searchQuery &&
        (initialOptions.length || passedData.length)
      )
        allData.push({
          key: "all",
          [optionValueProp]: "all",
          [optionLabelProp]: "All",
        });

      if (initialOptions.length)
        !searchQuery
          ? allData.push(...initialOptions)
          : allData.push(
              ...filterOptions(initialOptions, searchQuery, optionValueProp)
            );

      if (infiniteScroll) allData.push(...passedData);
      else {
        const sortedData = sort ? passedData.sort(dataSorter) : passedData;

        if (searchQuery) {
          const filteredOptions = filterOptions(
            passedData,
            searchQuery,
            optionLabelProp
          );
          allData.push(...filteredOptions);
        } else {
          allData.push(...sortedData);
        }
      }

      return allData;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [passedData, searchQuery]
  );

  const itemClickHandler = (datum) => () => {
    setState((st) => ({
      ...st,
      dropdownVisible: false,
    }));
    onChange(datum);
  };

  return (
    <>
      <CustomDropdown
        customClass={customClass}
        enableSearch={enableSearch}
        disabled={disabled}
        onBlur={onBlur}
        overlay={
          <DropdownMenu
            {...props}
            data={data}
            itemClickHandler={itemClickHandler}
            ref={ref}
            loadMoreData={loadMoreData}
            setParentState={setState}
            parentState={state}
            isDataLoading={isDataLoading}
          />
        }
        dropdownVisible={dropdownVisible}
        id={id}
        required={required}
        setParentState={setState}
        style={style}
        title={title}
        uuid={uuid}
      >
        {(!dropdownVisible || !enableSearch) && (
          <span
            className={`single-selected-item${
              !selItemValue || (selItemValue && dropdownVisible)
                ? " select-placeholder"
                : ""
            }`}
          >
            {displayValue}
          </span>
        )}
        {enableSearch && (
          <SearchBox
            className="single"
            data={data}
            disabled={disabled}
            dropdownVisible={dropdownVisible}
            infiniteScroll={infiniteScroll}
            loadMoreData={loadMoreData}
            placeholder={displayValue}
            ref={ref}
            setState={setState}
            value={searchQuery}
          />
        )}
        {showIcons && (
          <SingleSelectIcon
            allowClear={allowClear}
            defaultValue={defaultValue}
            disabled={disabled}
            dropdownVisible={dropdownVisible}
            onChange={onChange}
            optionValueProp={optionValueProp}
            setState={setState}
            showSearch={enableSearch}
            value={selItemValue}
          />
        )}
      </CustomDropdown>
    </>
  );
};

export default SingleSelect;

export const SingleFormikSelect = ({
  customClass,
  name,
  hideErrorMessage = false,
  onChange,
  loading,
  optionValueProp = "value",
  ...props
}) => {
  const [{ value }, { touched, error }, { setValue, setTouched }] = useField(
    name
  );

  const handleChange = useCallback(
    (value) => {
      setValue(value);
      if (onChange) onChange(value);
    },
    [setValue, onChange]
  );

  const errorMessage =
    typeof error === "object" ? getValueByKey(error, optionValueProp) : error;

  return (
    <>
      <SingleSelect
        {...props}
        onChange={handleChange}
        optionValueProp={optionValueProp}
        value={value}
        customClass={`${customClass} ${error && touched ? "error-box" : ""}`}
        onBlur={() => setTouched(true)}
        isDataLoading={loading}
      />
      {touched && errorMessage && !hideErrorMessage ? (
        <div className="error-label">{errorMessage}</div>
      ) : null}
    </>
  );
};
